'use client';
import React from 'react';
import { TagSearch, TagSearchProps, useLinkingSearchModule } from '..';
import { createSymbolSearchItem, isSymbolSearchItem, LinkingSearchItem, SearchItem } from '@benzinga/search-modules';
import { SearchLinkingIcon } from './SearchLinkingIcon';
import { useTickerLinking } from './useTickerLinking';
import { SearchBarForwardRef } from '../SearchBar';
import { WidgetLinkingManager } from '@benzinga/widget-linking';
import { SessionContext } from '@benzinga/session-context';
import { ClearTool, isClearTool, useClearTool } from '../SearchBar/Tools/ClearTool';
import { noop } from '@benzinga/utils';

interface Props extends Omit<TagSearchProps, 'tags' | 'onTagsUpdated'> {
  tag: SearchItem;
  onTagUpdated: (tag: SearchItem) => void;
}

export const TagSearchLinked: React.FC<Props> = React.memo(props => {
  const linkingModule = useLinkingSearchModule();
  const groupId = props.tag?.moduleId === linkingModule.id ? (props.tag as LinkingSearchItem).data.linkId : null;
  const [isOpen, setIsOpen] = React.useState(false);

  const linking = useTickerLinking(groupId);

  const session = React.useContext(SessionContext);
  const tagSearch = React.useRef<SearchBarForwardRef>(null);

  const onTickerClick = React.useCallback(() => {
    tagSearch.current?.focus();
  }, []);
  const icon = React.useMemo(() => {
    return (
      <SearchLinkingIcon
        modules={props.modules}
        onTickerClick={onTickerClick}
        showTag={!isOpen}
        tag={props.tag}
        thinUI={props.thinUI}
      />
    );
  }, [isOpen, onTickerClick, props.modules, props.tag, props.thinUI]);

  const [emptyTags, setEmptyTags] = React.useState(() => []);
  const syncTags = React.useCallback(() => {
    setEmptyTags([]);
  }, []);

  const localOnTagsUpdate = React.useCallback(
    (tags: SearchItem[]) => {
      const firstTag = tags.at(0);

      if (groupId !== null && firstTag && isSymbolSearchItem(firstTag)) {
        session.getManager(WidgetLinkingManager).getWidgetLinkFeedByID(groupId)?.pushEvent({
          symbol: firstTag.data.symbol,
          type: 'ticker_selected',
        });
      } else if (firstTag) {
        const onTagUpdated = props.onTagUpdated;
        onTagUpdated?.(firstTag);
      }
      syncTags();
    },
    [groupId, props.onTagUpdated, session, syncTags],
  );

  const onClearHandle = React.useCallback(
    (onClear?: () => void) => () => {
      const onTagUpdated = props.onTagUpdated;
      onTagUpdated(createSymbolSearchItem(linking?.currentTicker ?? 'AAPL'));
      onClear?.();
    },
    [linking?.currentTicker, props.onTagUpdated],
  );

  const internalClearTool = useClearTool(groupId !== null ? onClearHandle(noop) : undefined);

  const tools = React.useMemo(() => {
    let foundClearTool = false;
    const updateClearTool = (tool: ClearTool) => {
      const toolOnClear = tool.getOnClear();
      tool.setOnClear(toolOnClear || groupId !== null ? onClearHandle(toolOnClear) : undefined);
      tool.forceEnable(groupId !== null);
      foundClearTool = true;
      return tool;
    };
    const tools =
      props.tools?.map(tool => {
        if (isClearTool(tool)) {
          return updateClearTool(tool);
        }
        return tool;
      }) ?? [];
    if (!foundClearTool) {
      tools?.push(updateClearTool(internalClearTool as ClearTool));
    }
    return tools;
  }, [groupId, internalClearTool, onClearHandle, props.tools]);

  return (
    <TagSearch
      {...props}
      iconOverride={icon}
      onDropDownStateChange={setIsOpen}
      onTagsUpdated={localOnTagsUpdate}
      ref={tagSearch}
      tags={emptyTags}
      tools={tools}
    />
  );
});
